import { useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Timestamp } from 'firebase/firestore'

import { firestore } from '../firebase/firebaseConfig'
import { Person } from '../types'

type MonthlyDataGroups = {
  currentMonthLogins: number
  previousMonthLogins: number
  currentMonthLogs: number
  previousMonthLogs: number
}

export const useDataByMonth = (user: Person | null): MonthlyDataGroups => {
  const [data, setData] = useState<MonthlyDataGroups>({
    currentMonthLogins: 0,
    previousMonthLogins: 0,
    currentMonthLogs: 0,
    previousMonthLogs: 0,
  })

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return
      const now = dayjs()
      const currentMonth = now.month()
      const currentYear = now.year()
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1
      const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear

      const currentMonthLogins: Date[] = []
      const previousMonthLogins: Date[] = []

      user.logins?.forEach((ts: Timestamp) => {
        const date = dayjs(ts.toDate())
        const m = date.month()
        const y = date.year()

        if (m === currentMonth && y === currentYear) {
          currentMonthLogins.push(date.toDate())
        } else if (m === prevMonth && y === prevMonthYear) {
          previousMonthLogins.push(date.toDate())
        }
      })

      const currentStart = dayjs().startOf('month').format('YYYY-MM-DD')
      const currentEnd = dayjs().endOf('month').format('YYYY-MM-DD')
      const prevStart = dayjs()
        .subtract(1, 'month')
        .startOf('month')
        .format('YYYY-MM-DD')
      const prevEnd = dayjs()
        .subtract(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD')

      try {
        const logsCollectionRef = collection(
          firestore,
          'students',
          user.uid,
          'logs',
        )

        // Queries for current and previous month logs
        const currentMonthQuery = query(
          logsCollectionRef,
          where('date', '>=', currentStart),
          where('date', '<=', currentEnd),
        )

        const previousMonthQuery = query(
          logsCollectionRef,
          where('date', '>=', prevStart),
          where('date', '<=', prevEnd),
        )

        const [currentMonthSnapshot, previousMonthSnapshot] = await Promise.all(
          [getDocs(currentMonthQuery), getDocs(previousMonthQuery)],
        )

        setData({
          currentMonthLogins: currentMonthLogins.length,
          previousMonthLogins: previousMonthLogins.length,
          currentMonthLogs: currentMonthSnapshot.size,
          previousMonthLogs: previousMonthSnapshot.size,
        })
      } catch (error) {
        console.error('Error fetching logs:', error)
      }
    }

    fetchData()
  }, [user])

  const memoizedData = useMemo(() => data, [data])

  return memoizedData
}
